import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})

/**
 * This service handles subscriptions in the application
 * Since reused components don't call the ngOnDestroy function, subscriptions are managed here to avoid memory leaks
 * and produce singleton subscriptions when needed
 * */
export class SubscriptionHandlerService implements OnDestroy {
  // This map stores all subscriptions. Map<componentName, Map<subscriptionName, Subscription>>
  private subscriptions: Map<string, Map<string, Subscription>> = new Map();

  /**
   * Adds a subscription in singleton format
   *
   * @param componentName
   * @param subscriptionName
   * @param subscription
   * */
  addSubscription(componentName: string, subscriptionName: string, subscription: Subscription): void {
    // If the component is not yet stored in the map then it will be added
    if (!this.subscriptions.has(componentName)) {
      this.subscriptions.set(componentName, new Map());
    }

    // Unsubscribe previous subscription if it exists
    this.unsubscribe(componentName, subscriptionName);

    // Adds the subscription and associates it to the component
    this.subscriptions.get(componentName)!.set(subscriptionName, subscription);
  }

  /**
   * Unsubscribes from a subscription
   *
   * @param componentName
   * @param subscriptionName
   * */
  unsubscribe(componentName: string, subscriptionName: string): void {
    // Gets the component subscriptions
    const componentSubscriptions = this.subscriptions.get(componentName);

    // Unsubscribes and deletes the entry if the subscription is there
    if (componentSubscriptions?.has(subscriptionName)) {
      componentSubscriptions.get(subscriptionName)?.unsubscribe();
      componentSubscriptions.delete(subscriptionName);
    }
  }

  /**
   * Removes all subscriptions from a component
   *
   * @param componentName
   * */
  unsubscribeAllFromComponent(componentName: string): void {
    const componentSubscriptions = this.subscriptions.get(componentName);
    if (componentSubscriptions) {
      componentSubscriptions.forEach((subscription) => subscription.unsubscribe());
      this.subscriptions.delete(componentName);
    }
  }

  /**
   * Unsubscribes from all the subscriptions stored in the service
   * */
  unsubscribeAll(): void {
    this.subscriptions.forEach((componentSubscriptions) => {
      componentSubscriptions.forEach((subscription) => subscription.unsubscribe());
    });
    this.subscriptions.clear();
  }

  /**
   * Checks if the service has a subscription
   *
   * @param componentName
   * @param subscriptionName
   * */
  hasSubscription(componentName: string, subscriptionName: string): boolean {
    return this.subscriptions.get(componentName)?.get(subscriptionName) !== undefined;
  }

  /**
   * Gets a subscription
   *
   * @param componentName
   * @param subscriptionName
   * */
  getSubscription(componentName: string, subscriptionName: string): Subscription | undefined {
    return this.subscriptions.get(componentName)?.get(subscriptionName);
  }

  /**
   * This is used for debugging. Prints all subscriptions that are currently active.
   * By default, all subscriptions of all the components are returned.
   *
   * @param componentName optional name of the component to get the subscriptions from
   * */
  logAllSubscriptions(componentName?: string) {
    if (componentName) {
      const componentSubscriptions = this.subscriptions.get(componentName);
      console.warn(`[SUB][${componentName}]`, [...componentSubscriptions]);
    } else {
      console.warn(`[SUB][${[...this.subscriptions.keys()]}]`, [...this.subscriptions.values()]);
    }
  }

  /**
   * On destroying the service all subscriptions are unsubscribed from
   * */
  ngOnDestroy(): void {
    this.unsubscribeAll();
  }
}
